// src/components/feature/BlogArticleKit.tsx
//
// PAWTENANT-BLOG-PUBLISHING-SEO-CLUSTER-001
//
// The shared building blocks for the ESA-letter blog cluster's standalone
// article pages. They reproduce the layout /blog/esa-letter-requirements and
// /blog/2026-hud-esa-guidelines already use, so a new article in the cluster
// inherits the established design instead of restating 700 lines of markup.
//
// LIVE-ADAPTED (PAWTENANT-BLOG-SEO-CLUSTER-LIVE-ROLLOUT-002): the article H1
// uses `font-extrabold`, not TEST's `pt-hero-display`. That utility is defined
// in TEST's src/index.css and is NOT defined anywhere on LIVE, so porting it
// would ship headings with no weight at all. `font-extrabold` is what every
// existing LIVE standalone blog article already uses.
//
// Every component is declared at MODULE level. Declaring a component inside
// another component's render body creates a new element type on each render,
// which remounts the subtree and makes an open FAQ answer or a focused input
// jump — a defect this repo has already paid for once.
//
// The FAQ accordion and <BlogArticleSchema> both read the SAME array out of
// src/seo/blog/esaLetterCluster.ts. That is the whole reason visible and
// structured FAQ copy cannot drift apart here.

import { Link } from "react-router-dom";
import { CLUSTER_SSR_ATTR, type ClusterFaq } from "@/seo/blog/esaLetterCluster";

export const inlineLink =
  "text-orange-600 font-semibold hover:text-orange-700 underline decoration-orange-200 underline-offset-2";

/* ── Typography ──────────────────────────────────────────────────────────── */

export function SectionHeading({ id, children }: { id?: string; children: React.ReactNode }) {
  return (
    <h2 id={id} className="text-xl md:text-2xl font-extrabold text-gray-900 mt-12 mb-4 scroll-mt-28">
      {children}
    </h2>
  );
}

export function SubHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-base md:text-lg font-bold text-gray-900 mt-8 mb-3">{children}</h3>;
}

export function Para({ children }: { children: React.ReactNode }) {
  return <p className="text-sm md:text-[15px] text-gray-600 leading-relaxed mb-4">{children}</p>;
}

export function CheckList({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="space-y-2.5 my-5">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-2.5 text-sm md:text-[15px] text-gray-600 leading-relaxed">
          <i className="ri-checkbox-circle-line text-orange-500 mt-0.5 flex-shrink-0"></i>
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

export function NumberedList({ items }: { items: React.ReactNode[] }) {
  return (
    <ol className="space-y-3 my-5">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-3 text-sm md:text-[15px] text-gray-600 leading-relaxed">
          <span className="mt-0.5 w-6 h-6 flex-shrink-0 rounded-full bg-orange-100 text-orange-600 text-xs font-bold flex items-center justify-center">
            {i + 1}
          </span>
          <span>{it}</span>
        </li>
      ))}
    </ol>
  );
}

/* ── Tables ──────────────────────────────────────────────────────────────── */

/**
 * A comparison table that stays usable on a phone. The wrapper scrolls
 * horizontally rather than letting the table force the page wide, which is the
 * usual cause of body-level horizontal overflow on article pages.
 */
export function DataTable({
  head,
  rows,
  caption,
}: {
  head: string[];
  rows: React.ReactNode[][];
  caption?: string;
}) {
  return (
    <figure className="my-6">
      <div className="overflow-x-auto rounded-2xl border border-gray-200">
        <table className="w-full min-w-[34rem] text-left text-sm">
          <thead className="bg-[#fdf6ee]">
            <tr>
              {head.map((h) => (
                <th key={h} scope="col" className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-gray-600">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r, i) => (
              <tr key={i} className="align-top">
                {r.map((cell, j) => (
                  <td key={j} className="px-4 py-3 text-gray-600 leading-relaxed">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {caption ? <figcaption className="mt-2 text-xs text-gray-400">{caption}</figcaption> : null}
    </figure>
  );
}

/* ── Callouts ────────────────────────────────────────────────────────────── */

export function Callout({
  tone = "amber",
  icon = "ri-alert-line",
  title,
  children,
}: {
  tone?: "amber" | "orange" | "gray";
  icon?: string;
  title: string;
  children: React.ReactNode;
}) {
  const box =
    tone === "orange"
      ? "bg-orange-50 border-orange-200"
      : tone === "gray"
        ? "bg-[#fafafa] border-gray-200"
        : "bg-amber-50 border-amber-200";
  const label =
    tone === "orange" ? "text-orange-700" : tone === "gray" ? "text-gray-500" : "text-amber-700";
  return (
    <div className={`my-6 rounded-2xl border p-5 sm:p-6 ${box}`}>
      <p className={`text-[11px] font-bold uppercase tracking-widest mb-3 flex items-center gap-2 ${label}`}>
        <i className={icon}></i> {title}
      </p>
      <div className="space-y-2 text-sm text-gray-700 leading-relaxed">{children}</div>
    </div>
  );
}

/** The 2025–2026 federal timeline. One wording, reused, so the dates cannot drift. */
export function FederalPolicyTimeline() {
  return (
    <Callout title="2025–2026 federal ESA policy timeline">
      <ul className="space-y-2">
        <li>
          <strong className="text-gray-900">September 17, 2025:</strong> HUD withdrew guidance
          documents FHEO-2020-01 and FHEO-2013-01, removing the documentation framework landlords
          and tenants had relied on for a decade.
        </li>
        <li>
          <strong className="text-gray-900">May 22, 2026:</strong> HUD issued an enforcement
          memorandum directing its Office of Fair Housing and Equal Opportunity to find reasonable
          cause only where an animal is individually trained to perform work or tasks related to the
          complainant&apos;s disability — the standard the ADA applies to service animals.
        </li>
        <li>
          <strong className="text-gray-900">What did NOT change:</strong> the Fair Housing Act
          itself (42 U.S.C. §3604(f)) and its regulations, your right to bring a private FHA suit,
          and state and local fair-housing laws, which the memorandum does not change.
        </li>
      </ul>
    </Callout>
  );
}

/* ── Hero ────────────────────────────────────────────────────────────────── */

export function ArticleHero({
  breadcrumb,
  parent = { to: "/blog", label: "Blog" },
  chips,
  h1,
  standfirst,
  quickAnswer,
  facts,
  image,
  imageAlt,
  imageWidth,
  imageHeight,
}: {
  breadcrumb: string;
  /** Middle breadcrumb. Defaults to the blog; the ESA housing guides use the Resource Center. */
  parent?: { to: string; label: string };
  chips: string[];
  h1: string;
  standfirst: React.ReactNode;
  /** Optional: a page whose approved copy has no short answer omits the box. */
  quickAnswer?: React.ReactNode;
  facts?: React.ReactNode[];
  image: string;
  imageAlt: string;
  imageWidth: number;
  imageHeight: number;
}) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-orange-50 via-[#fffaf4] to-white pt-28 pb-12 sm:pt-32 sm:pb-14">
      <div className="max-w-3xl mx-auto px-5 sm:px-6">
        <nav className="text-xs text-gray-400 mb-5" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-orange-600">Home</Link>
          <span className="mx-1.5">/</span>
          <Link to={parent.to} className="hover:text-orange-600">{parent.label}</Link>
          <span className="mx-1.5">/</span>
          <span className="text-gray-500">{breadcrumb}</span>
        </nav>

        <div className="flex flex-wrap gap-2 mb-5">
          {chips.map((chip) => (
            <span
              key={chip}
              className="text-[11px] font-semibold text-orange-600 bg-white border border-orange-200 rounded-full px-3 py-1 shadow-sm"
            >
              {chip}
            </span>
          ))}
        </div>

        <h1 className="text-3xl md:text-[42px] font-extrabold text-gray-900 leading-tight mb-4">{h1}</h1>

        <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6">{standfirst}</p>

        {quickAnswer ? (
        <div className="rounded-2xl bg-white border border-orange-100 shadow-[0_18px_40px_-28px_rgba(122,78,45,0.4)] p-5 sm:p-6">
          <p className="text-[11px] font-bold uppercase tracking-widest text-orange-600 mb-2.5 flex items-center gap-2">
            <i className="ri-flashlight-line"></i> Short answer
          </p>
          <p className="text-sm md:text-[15px] text-gray-700 leading-relaxed">{quickAnswer}</p>
          {facts?.length ? (
            <ul className="mt-4 pt-4 border-t border-orange-100 space-y-2 text-sm text-gray-600 leading-relaxed">
              {facts.map((f, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <i className="ri-checkbox-circle-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        ) : null}

        <figure className="mt-8">
          <img
            src={image}
            alt={imageAlt}
            width={imageWidth}
            height={imageHeight}
            fetchPriority="high"
            decoding="async"
            className="w-full h-52 sm:h-80 md:h-[26rem] object-cover object-center rounded-3xl border border-orange-100 shadow-[0_24px_60px_-30px_rgba(122,78,45,0.35)]"
          />
        </figure>
      </div>
    </section>
  );
}

/* ── Contents ────────────────────────────────────────────────────────────── */

export function TableOfContents({ items }: { items: { id: string; label: string }[] }) {
  return (
    <nav aria-label="On this page" className="my-8 rounded-2xl border border-gray-200 bg-[#fafafa] p-5">
      <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-3">On this page</p>
      <ol className="space-y-1.5">
        {items.map((it, i) => (
          <li key={it.id} className="text-sm text-gray-600 leading-relaxed">
            <span className="text-gray-400 mr-2">{i + 1}.</span>
            <a href={`#${it.id}`} className={inlineLink}>
              {it.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

/* ── Sources ─────────────────────────────────────────────────────────────── */

export function SourcesBox({ items }: { items: React.ReactNode[] }) {
  return (
    <div className="mt-10 rounded-xl bg-[#fafafa] border border-gray-100 p-5">
      <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-2.5">Sources &amp; further reading</p>
      <ul className="space-y-1.5 text-xs text-gray-600">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2">
            <i className="ri-external-link-line text-gray-400 mt-0.5"></i>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── FAQ ─────────────────────────────────────────────────────────────────── */

/**
 * The visible FAQ accordion.
 *
 * WHY <details>/<summary> AND NOT A useState TOGGLE
 * A state-driven accordion renders only the OPEN answer, so the prerendered
 * HTML a crawler reads carries every question but exactly one answer — while
 * the FAQPage block in <head> carries all of them. That is a visible/schema
 * mismatch by construction, and it is the specific defect
 * scripts/check-blog-esa-cluster-seo.mjs exists to catch. <details> puts every
 * answer in the DOM, collapsed, which is both what Google documents as
 * acceptable for expandable FAQ content and what makes the parity assertion
 * checkable from the built output. It also works with JavaScript disabled and
 * is keyboard-accessible without any ARIA of our own.
 *
 * `data-pt-faq-q` / `data-pt-faq-a` are the hooks the guard reads. They are the
 * contract: renaming them silently disables the parity check, so the guard
 * fails when they are missing rather than when they differ.
 */
export function ArticleFaqs({ heading, faqs }: { heading: string; faqs: readonly ClusterFaq[] }) {
  return (
    <section className="py-14 bg-white">
      <div className="max-w-3xl mx-auto px-5 sm:px-6">
        <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-8 text-center">{heading}</h2>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <details
              key={f.q}
              open={i === 0}
              className="group border border-gray-200 rounded-xl overflow-hidden [&_summary::-webkit-details-marker]:hidden"
            >
              <summary className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors list-none">
                <span data-pt-faq-q className="text-sm font-bold text-gray-900">
                  {f.q}
                </span>
                <i className="ri-arrow-down-s-line text-gray-400 text-lg flex-shrink-0 transition-transform group-open:rotate-180"></i>
              </summary>
              <div className="px-5 pb-4 -mt-1">
                <p data-pt-faq-a className="text-sm text-gray-600 leading-relaxed">
                  {f.a}
                </p>
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── CTA + related ───────────────────────────────────────────────────────── */

export function BottomCta({
  heading,
  body,
  assessmentHref,
  secondary,
}: {
  heading: string;
  body: React.ReactNode;
  /** Already run through withAttribution() by the page. */
  assessmentHref: string;
  secondary: { to: string; label: string; icon: string };
}) {
  return (
    <section className="py-12 bg-gradient-to-br from-orange-500 to-orange-600">
      <div className="max-w-4xl mx-auto px-5 sm:px-6 text-center">
        <h2 className="text-xl md:text-2xl font-extrabold text-white mb-3">{heading}</h2>
        <p className="text-orange-50 text-sm md:text-base mb-6 max-w-2xl mx-auto">{body}</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to={assessmentHref}
            className="whitespace-nowrap inline-flex items-center gap-2 px-7 py-3.5 bg-white text-orange-600 font-bold rounded-xl hover:bg-orange-50 transition-colors text-sm shadow-sm"
          >
            <i className="ri-stethoscope-line"></i> Start assessment
          </Link>
          <Link
            to={secondary.to}
            className="whitespace-nowrap inline-flex items-center gap-2 px-7 py-3.5 bg-orange-400/30 text-white font-bold rounded-xl border border-white/40 hover:bg-orange-400/50 transition-colors text-sm"
          >
            <i className={secondary.icon}></i> {secondary.label}
          </Link>
        </div>
      </div>
    </section>
  );
}

/**
 * A compact assessment prompt for the body of an article — the in-flow
 * counterpart of <BottomCta>, with the same primary button.
 */
export function InlineCta({
  heading,
  body,
  note,
  assessmentHref,
}: {
  heading: string;
  body: React.ReactNode;
  note?: React.ReactNode;
  /** Already run through withAttribution() by the page. */
  assessmentHref: string;
}) {
  return (
    <aside className="my-8 rounded-2xl border border-orange-200 bg-orange-50 p-5 sm:p-6">
      <p className="text-base md:text-lg font-extrabold text-gray-900 mb-2">{heading}</p>
      <p className="text-sm text-gray-700 leading-relaxed mb-4">{body}</p>
      <Link
        to={assessmentHref}
        className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-white font-bold rounded-xl hover:bg-orange-600 transition-colors text-sm shadow-sm"
      >
        <i className="ri-stethoscope-line"></i> Start assessment
      </Link>
      {note ? <p className="mt-3 text-xs text-gray-500 leading-relaxed">{note}</p> : null}
    </aside>
  );
}

export function KeepReading({ links }: { links: { to: string; icon: string; label: string }[] }) {
  return (
    <section className="py-12 bg-[#fdf6ee] border-t border-orange-100">
      <div className="max-w-4xl mx-auto px-5 sm:px-6">
        <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-5 text-center">Keep reading</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 items-stretch">
          {links.map((r) => (
            <Link
              key={r.to}
              to={r.to}
              className="flex items-center gap-3 h-full bg-white border border-orange-100 rounded-xl px-4 py-3.5 hover:border-orange-300 hover:shadow-sm transition-all"
            >
              <i className={`${r.icon} text-orange-500 text-lg flex-shrink-0`}></i>
              <span className="text-sm font-semibold text-gray-800">{r.label}</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function LegalDisclaimer({ children }: { children: React.ReactNode }) {
  return (
    <section className="py-10 bg-white border-t border-gray-100">
      <div className="max-w-3xl mx-auto px-5 sm:px-6">
        <div className="flex items-start gap-3">
          <i className="ri-information-line text-gray-400 text-lg mt-0.5"></i>
          <p className="text-xs text-gray-500 leading-relaxed">
            <strong className="text-gray-600">Informational only — not legal advice.</strong> {children}
          </p>
        </div>
      </div>
    </section>
  );
}

/** The shared closing paragraph, so the standing disclaimer is written once. */
export function StandardDisclaimerBody() {
  return (
    <>
      This guide from the PawTenant Editorial Team references 42 U.S.C. §3604(f), HUD&apos;s
      September 17, 2025 withdrawal of Notices FHEO-2020-01 and FHEO-2013-01, HUD&apos;s May 22, 2026
      FHEO enforcement memorandum, and the state statutes named above. It is not a substitute for
      advice from a licensed attorney, and it is not medical advice. Laws and enforcement policies
      change and vary by state. PawTenant connects you with licensed professionals who decide
      whether documentation is appropriate; it does not sell ESA registrations, claim any government
      affiliation, or guarantee landlord approval, fee waivers, or any legal outcome. For your
      situation, consult a fair-housing attorney or your state fair-housing agency.
    </>
  );
}

/* ── Structured data ─────────────────────────────────────────────────────── */

/**
 * Emits the article's BlogPosting + BreadcrumbList + FAQPage graph — but ONLY
 * when the build-time head script is absent.
 *
 * TWO RENDER PATHS, ONE COPY OF EACH ENTITY
 *   * Build time: scripts/prerender-full-body-spike.mjs strips every JSON-LD
 *     block out of the SSR body and injects this same graph into <head>
 *     instead (see getRouteHeadJsonLd in src/prerender/entry.tsx), marked with
 *     CLUSTER_SSR_ATTR. There is no `window` here, so this renders nothing.
 *   * Browser: createRoot() only replaces #root, so that head script survives
 *     hydration and this still renders nothing. On a client-side navigation or
 *     on the dev server the head script is absent, and this emits the graph.
 */
export function BlogArticleSchema({ route, graph }: { route: string; graph: Record<string, unknown> }) {
  if (typeof window === "undefined" || typeof document === "undefined") return null;
  if (document.head.querySelector(`script[${CLUSTER_SSR_ATTR}="${CSS.escape(route)}"]`)) return null;
  const json = JSON.stringify(graph).replace(/</g, "\\u003c");
  return <script type="application/ld+json" data-pt-article={route} dangerouslySetInnerHTML={{ __html: json }} />;
}
